// Intercepteur global de fetch : injecte le Bearer token sur TOUS les appels
// vers notre backend API.
//
// Contexte : l'API n'est plus ouverte en anonyme (porte d'auth cote backend).
// Or de nombreux appels du frontend (smartFetch sans header, ~44 fetch bruts
// dans les ecrans) n'attachaient pas le token — ils fonctionnaient uniquement
// parce que l'API repondait sans authentification. Depuis le durcissement, ils
// renvoient 401. Plutot que de corriger 44 sites d'appel, on patche window.fetch
// une seule fois au demarrage pour ajouter l'en-tete Authorization aux requetes
// destinees a notre API (jamais aux routes /auth/, jamais aux hotes tiers, et
// jamais si un Authorization est deja present).

import { API_BASE_URL } from '../config/constants';
import { notify } from './notify';

let installed = false;

function cibleApi(url: string): boolean {
  const u = String(url || '');
  if (!u) return false;
  if (u.includes('/auth/')) return false;          // login/register/verify : pas de token injecte
  if (u.startsWith(API_BASE_URL)) return u.includes('/api/'); // URL absolue vers notre backend
  if (u.startsWith('/api/')) return true;           // chemin relatif same-origin (web servi par Railway)
  return false;                                     // hotes tiers (Supabase Storage, etc.) : intacts
}

export function installAuthInterceptor(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const url =
        typeof input === 'string' ? input :
        input instanceof URL ? input.href :
        (input as Request)?.url || '';

      if (cibleApi(url)) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const headers = new Headers(
            (init && init.headers) ||
            (input instanceof Request ? input.headers : undefined)
          );
          if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
            init = { ...(init || {}), headers };
          }
        }
      }
    } catch {
      // Ne jamais casser un fetch a cause de l'intercepteur.
    }
    return originalFetch(input as any, init).then((rep) => {
      if (rep.status === 401) signalerSessionExpiree(input);
      // ⚠️ Un 403 dit que la vue LOCALE des droits est perimee, a l'instant
      // meme ou l'ecart devient visible : c'est le signal le plus fiable qu'on
      // puisse avoir. On relit les droits, et l'ecran se corrige tout seul au
      // lieu de proposer indefiniment une action refusee. Jamais sur `/auth/`
      // (dont fait partie la requete de rafraichissement elle-meme) : ce serait
      // une boucle.
      if (rep.status === 403) signalerRefusDroits(input);
      return rep;
    });
  };
}

/**
 * Session expiree : rien ne le detectait, et le symptome etait trompeur.
 *
 * Vecu le 2026-07-27 : apres une rotation du JWT_SECRET, le jeton garde en
 * localStorage n'etait plus valide. Chaque ecran recevait un 401, aucun ne
 * l'affichait — l'utilisateur a vu des listes VIDES et en a conclu que ses
 * donnees avaient disparu. Elles etaient intactes. Un ecran vide et un acces
 * refuse doivent se distinguer a l'oeil.
 *
 * On efface donc la session et on ramene a la connexion, avec un message.
 * Volontairement declenche UNE SEULE FOIS (plusieurs appels partent en
 * parallele au chargement d'un ecran) et jamais sur les routes /auth/, dont
 * un 401 signifie « identifiants incorrects », pas « session expiree ».
 */
/**
 * Refus de droits (403) : on previent l'abonne, sans dependre de lui.
 *
 * L'intercepteur est installe au demarrage, avant tout le reste ; il ne doit
 * donc importer aucun module applicatif (cycle). On passe par un callback
 * enregistre — meme patron que `registerOfflineNotice` dans fetchManager.
 */
let surRefusDroits: (() => void) | null = null;
export function registerRefusDroits(cb: () => void): void { surRefusDroits = cb; }

function signalerRefusDroits(input: RequestInfo | URL): void {
  try {
    const url =
      typeof input === 'string' ? input :
      input instanceof URL ? input.href :
      (input as Request)?.url || '';
    if (url.includes('/auth/')) return;          // dont /auth/verify : pas de boucle
    surRefusDroits?.();
  } catch { /* ne jamais casser un fetch */ }
}

let sessionExpireeSignalee = false;
function signalerSessionExpiree(input: RequestInfo | URL): void {
  try {
    const url =
      typeof input === 'string' ? input :
      input instanceof URL ? input.href :
      (input as Request)?.url || '';
    if (url.includes('/auth/')) return;
    if (!localStorage.getItem('auth_token')) return;   // deja deconnecte
    if (sessionExpireeSignalee) return;
    sessionExpireeSignalee = true;

    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // notify() et NON window.alert : alert() gele la saisie clavier sous
    // Electron (regle du projet, cf. utils/notify.tsx). On laisse le message
    // s'afficher avant de recharger.
    notify("Session expirée. Vos données sont intactes — reconnectez-vous.", "error");
    setTimeout(() => window.location.reload(), 2200);
  } catch {
    // Ne jamais casser un fetch a cause de l'intercepteur.
  }
}
